import i18n from 'i18next';
import { create } from "zustand";
import { formService } from "@/services";
import { isPreviewMode } from "@/lib/previewMode";
import { useGuestRegistrationStore } from "./guestRegistrationStore";
import {
    FormData,
    FormMetadata,
    FormState,
    FileUploadTracker,
    BookingDetails,
    ContractState,
    ContractSigningState,
    ContractSignature,
    GuestData,
} from "@/types/form";
import {
    PublicFormDataApiDto,
    PublicFormDataAndVersionApiDto,
    VersionApiDto,
    FormStatusApiEnum,
    ContractApiDto,
    ContractSignatureApiDto,
    MimeApiEnum,
} from "@/types/api";
import { UIStore } from "@/types/store";
import {
    convertApiFormDataToLocal,
    convertLocalFormDataToApi,
} from "@/utils/transformApiData";
import { UploadedFileInfo } from "@/types/form";
import type { FormGuestEntry, ProfileMatch } from "@/types/guest";

interface GeneralFormStoreState {
    formData: FormData | null;
    formMetadata: FormMetadata | null;
    bookingDetails: BookingDetails | null;
    formState: FormState;
    fileUploadTracker: FileUploadTracker;
    formId: string;
    contractState: ContractState;
    contractSigningState: ContractSigningState;
    uiStore: UIStore | null;

    // Owner's branded subdomain hint emitted by _getFormData when the form's listing's
    // Owner has an active Business-tier brand. FormDataBrandRedirect subscribes to
    // this slice to issue the canonical→branded redirect, gated additionally by
    // brandAutoRedirect; null when the form's Owner has no brand or is below Business tier.
    brandSlug: string | null;

    // Owner's opt-out of the canonical→branded force-redirect. Gates the FORWARD redirect
    // only (reverse + slug-history redirects are never gated). Defaults true (column
    // DEFAULT TRUE); harmless before the fetch settles because the forward branch also
    // requires brandSlug.
    brandAutoRedirect: boolean;

    // WHY: FormDataBrandRedirect uses this as the load sentinel. brandSlug alone is
    // ambiguous — it's null both before the fetch completes AND after a successful
    // fetch on a Basic-tier listing — so the redirect cannot interpret null as
    // "no brand" without first knowing the fetch settled. formId is set BEFORE the
    // fetch starts (line ~124) so it can't be the sentinel; formMetadata flips on
    // success but stays null on the error path, trapping the reverse redirect on
    // failed fetches. formLoaded flips to true on BOTH the success and error
    // terminals so the redirect is unblocked either way.
    formLoaded: boolean;

    // Story 20-2 Phase 1: per-entry returning-guest profile match (in-memory only,
    // no localStorage / IndexedDB / persist middleware — wiped on browser refresh).
    profileMatchByEntryId: Record<string, ProfileMatch>;
    // Story 20-2 Phase 1: per-entry edited-field set the user typed away from the
    // prefilled value. Set<string> is not JSON-serializable so this slice MUST stay
    // unpersisted.
    editedFieldsByEntryId: Record<string, Set<string>>;

    // Actions
    initializeForm: (publicId: string, guestEntries?: FormGuestEntry[]) => Promise<void>;
    saveForm: () => Promise<void>;
    submitForm: () => Promise<void>;
    checkVersion: (signal?: AbortSignal) => Promise<boolean>;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    reset: () => void;
    updateFromServer: (serverData: PublicFormDataAndVersionApiDto, guestEntries?: FormGuestEntry[]) => void;
    updateGuestLocal: (entryId: string, data: Partial<GuestData>) => void;
    updateGuestFiles: (entryId: string, files: UploadedFileInfo[]) => void;
    updateGuestFilesFromServer: (entryId: string, serverData: PublicFormDataAndVersionApiDto) => void;
    updateFormStatus: (status: FormStatusApiEnum) => void;
    loadContract: (signal?: AbortSignal) => Promise<void>;
    signContract: (signature: ContractSignature) => Promise<void>;
    setContractError: (error: string | null) => void;
    refreshFormData: (publicId: string, signal?: AbortSignal, guestEntries?: FormGuestEntry[]) => Promise<void>;
    resetContractState: () => void;
    updateGuestDesignation: (entryId: string) => void;
    // Story 20-2 Phase 1.
    setProfileMatch: (entryId: string, match: ProfileMatch | null) => void;
    addEditedField: (entryId: string, fieldName: string) => void;
    // Story 20-5 Phase 3 — Cancel-without-typing data-wipe-trap fix (AC #6).
    removeEditedField: (entryId: string, fieldName: string) => void;
    // Claim 1 follow-up — drop the entire edited-fields slot for an entry. Used by
    // useResetProfileMatchOnIdentityEdit when the lookup-key triple changes after POST.
    clearEditedFields: (entryId: string) => void;
}

export const useGeneralFormStore = create<GeneralFormStoreState>((set, get) => ({
    formData: null,
    formMetadata: null,
    bookingDetails: null,
    formState: {
        isLoading: false,
        isSaving: false,
        isSubmitting: false,
        hasUnsavedChanges: false,
        lastSavedVersion: "",
        currentVersion: "",
        error: undefined,
        currentSection: "",
        sections: {},
        versionMismatch: false,
    },
    fileUploadTracker: {},
    formId: "",
    uiStore: null,
    profileMatchByEntryId: {},
    editedFieldsByEntryId: {},
    brandSlug: null,
    brandAutoRedirect: true,
    formLoaded: false,
    contractState: {
        isLoading: false,
        data: null,
        error: null,
    },
    contractSigningState: {
        isLoading: false,
        isSigning: false,
        error: null,
    },

    initializeForm: async (publicId: string, guestEntries?: FormGuestEntry[]) => {
        // # WHY: PreviewDataProvider hydrates this store synchronously before the
        // children mount; bailing here keeps the FormPage useEffect path identical
        // in preview mode without touching real services or flipping isLoading.
        if (isPreviewMode()) return;

        set((state) => ({
            formState: { ...state.formState, isLoading: true },
        }));

        try {
            set({ formId: publicId });

            const serverResponse: PublicFormDataApiDto = await formService.getForm(publicId);

            if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                console.error('[initializeForm] Invalid server response structure', { serverResponse });
                throw new Error('Invalid server response: Missing required fields');
            }

            set({ brandSlug: serverResponse.brandSlug ?? null });
            set({ brandAutoRedirect: serverResponse.brandAutoRedirect ?? true });

            get().updateFromServer({
                data: serverResponse.data,
                version: serverResponse.version,
            }, guestEntries);

            const formMetadata: FormMetadata = {
                formId: publicId,
                publicId: publicId,
                templateId: serverResponse.templateId,
                listingTitle: serverResponse.listingTitle,
                checkInDate: serverResponse.checkInDate,
                checkOutDate: serverResponse.checkOutDate,
                numberOfGuests: serverResponse.numberOfGuests,
                maxVehicles: serverResponse.maxVehicles,
                allowPets: serverResponse.allowPets,
                listingAddress: serverResponse.listingAddress,
                listingCity: serverResponse.listingCity,
                listingCountry: serverResponse.listingCountry,
                latitude: serverResponse.latitude,
                longitude: serverResponse.longitude,
                status: serverResponse.status,
                version: serverResponse.version,
                requiredGuestImages: serverResponse.requiredGuestImages,
                requiredBookingContract: serverResponse.requiredBookingContract,
                signedContract: serverResponse.signedContract,
                minAgeToRequireImages: serverResponse.minAgeToRequireImages,
                requireContactDataFromAllGuests: serverResponse.requireContactDataFromAllGuests,
                // Migration 0160: normalize the wire's optional booleans to concrete ones
                // exactly once, here. A backend that predates 0160 omits them entirely, and
                // absent must mean "unrestricted" — the pre-0160 behaviour — not "unknown".
                requirePassportForForeignGuests: serverResponse.requirePassportForForeignGuests === true,
                requireNationalIdForDomesticGuests: serverResponse.requireNationalIdForDomesticGuests === true,
                overallProgress: serverResponse.overallProgress,
                contractStatus: serverResponse.contractStatus,
                rejectionReason: serverResponse.rejectionReason,
                // Story 28-4: full bilingual + multi-status messages map.
                // Always present on the wire (possibly empty). FormPage
                // resolves (status, locale) at render time.
                formMessages: serverResponse.formMessages ?? {},
            };
            const bookingDetails: BookingDetails = {
                listingTitle: serverResponse.listingTitle,
                checkInDate: serverResponse.checkInDate,
                checkOutDate: serverResponse.checkOutDate,
                numberOfGuests: serverResponse.numberOfGuests,
                maxVehicles: serverResponse.maxVehicles,
                allowPets: serverResponse.allowPets,
                listingAddress: serverResponse.listingAddress,
                listingCity: serverResponse.listingCity,
                listingCountry: serverResponse.listingCountry,
                latitude: serverResponse.latitude,
                longitude: serverResponse.longitude,
            };

            set((state) => ({
                formMetadata,
                bookingDetails,
                formState: {
                    ...state.formState,
                    currentVersion: serverResponse.version,
                    lastSavedVersion: serverResponse.version,
                },
            }));
        } catch (error) {
            console.error("Error initializing form", error);
            get().setError("Failed to initialize form.");
            throw error;
        } finally {
            set((state) => ({
                formState: { ...state.formState, isLoading: false },
                formLoaded: true,
            }));
        }
    },

    saveForm: async () => {
        if (isPreviewMode()) return;
        set((state) => ({
            formState: { ...state.formState, isSaving: true },
        }));

        try {
            const { formData, formId, formState, uiStore } = get();
            if (!formData) {
                console.error("No form data to save");
                return;
            }

            const apiData = convertLocalFormDataToApi(formData);
            const response: VersionApiDto = await formService.updateFormData(formId, {
                data: apiData,
                version: formState.currentVersion,
            });

            set((state) => ({
                formState: {
                    ...state.formState,
                    currentVersion: response.version,
                    lastSavedVersion: response.version,
                },
            }));

            // Save succeeded. Refresh form data to sync contract validity (hash may have
            // changed). NOTE: refreshFormData only re-throws NOT_FOUND errors (other errors
            // are swallowed internally), so the catch here only fires when the form was
            // deleted server-side between save and refresh. In that case the save worked
            // but the form is gone — let the FormPage polling/visibility handler navigate
            // the user to the not-found state by re-throwing. The save success toast is
            // skipped because the outer catch will fire instead.
            // # WHY guestEntries: refreshFormData feeds convertApiFormDataToLocal, which
            // resolves real UUIDs for formData.guests[i].entryId only when entries are
            // provided — otherwise it falls back to legacy_<num> placeholders that don't
            // match useGuestRegistrationStore.guests[i].id, breaking the GuestSection
            // per-card progress lookup until the next poll/visibility refresh.
            try {
                const guestEntries = useGuestRegistrationStore.getState().guests;
                await get().refreshFormData(formId, undefined, guestEntries);
            } catch (refreshError) {
                console.warn(
                    "[saveForm] Save succeeded but refresh reported NOT_FOUND — form was deleted server-side",
                    refreshError,
                );
                throw refreshError;
            }

            uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.saved.title"),
                message: i18n.t("toasts.form.saved.message"),
                duration: 3000,
            });
        } catch (error) {
            console.error("Error saving form", error);
            get().setError("Failed to save form.");
            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.saveError.title"),
                message: i18n.t("toasts.form.saveError.message"),
                duration: 3000,
            });
        } finally {
            set((state) => ({
                formState: { ...state.formState, isSaving: false },
            }));
        }
    },

    submitForm: async () => {
        if (isPreviewMode()) return;
        set((state) => ({
            formState: { ...state.formState, isSubmitting: true },
        }));

        try {
            const { formId, formState } = get();

            await formService.completeForm(formId, {
                version: formState.currentVersion,
            });

            get().updateFormStatus(FormStatusApiEnum.COMPLETED);

            set((state) => ({
                formState: {
                    ...state.formState,
                    hasUnsavedChanges: false,
                },
            }));

            get().uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.submitted.title"),
                message: i18n.t("toasts.form.submitted.message"),
                duration: 3000,
            });

        } catch (error: unknown) {
            console.error("Error submitting form", error);
            const axiosErr = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage = axiosErr.response?.data?.message || axiosErr.message || "Failed to submit form";
            get().setError(errorMessage);
            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.submissionError.title"),
                message: errorMessage,
                duration: 3000,
            });
            throw error;
        } finally {
            set((state) => ({
                formState: { ...state.formState, isSubmitting: false },
            }));
        }
    },

    checkVersion: async (signal?: AbortSignal) => {
        if (isPreviewMode()) return true;
        try {
            const { formId, formState, uiStore } = get();

            if (!formId) return true;
            if (!formState.currentVersion || formState.currentVersion === "") return true;

            const response = await formService.isLatestFormDataVersion(formId, {
                version: formState.currentVersion,
            }, signal);

            if (signal?.aborted) return true;

            if (!response.result) {
                console.log('[checkVersion] Version mismatch detected, fetching latest data');

                try {
                    const serverResponse = await formService.getForm(formId, signal);
                    if (signal?.aborted) return true;

                    if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                        throw new Error('Invalid server response: Missing required fields');
                    }

                    // convertApiFormDataToLocal now resolves entryId from each guest's own
                    // guestEntryId, so the entryId is already authoritative. Only fall back to
                    // correlating against existing local guests (by guestNumId) when the server
                    // response lacked guestEntryId and left a legacy_* placeholder — never clobber
                    // a real entryId (a guestNumId match can be wrong after a renumber).
                    const existingGuests = get().formData?.guests;
                    const convertedData = convertApiFormDataToLocal(serverResponse.data);
                    if (existingGuests) {
                        convertedData.guests = convertedData.guests.map(g => {
                            if (!g.entryId.startsWith('legacy_')) return g;
                            const existing = existingGuests.find(e => e.guestNumId === g.guestNumId);
                            return existing ? { ...g, entryId: existing.entryId } : g;
                        });
                    }
                    set({ formData: convertedData });

                    set((state) => ({
                        formState: {
                            ...state.formState,
                            currentVersion: serverResponse.version,
                            lastSavedVersion: serverResponse.version,
                        },
                    }));

                    return false;
                } catch (conversionError: unknown) {
                    console.error('[checkVersion] Failed to process server data:', conversionError);
                    uiStore?.addToast({
                        type: "warning",
                        title: i18n.t("toasts.form.syncIssue.title"),
                        message: i18n.t("toasts.form.syncIssue.message"),
                        duration: 3000,
                    });
                    return true;
                }
            }

            return true;
        } catch (error: unknown) {
            const errObj = error as { name?: string; response?: unknown; code?: string; message?: string };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                return true;
            }

            console.error("[checkVersion] Error checking version", error);

            const isNetworkError =
                !errObj.response ||
                errObj.code === 'ECONNABORTED' ||
                errObj.message?.toLowerCase().includes('network');

            if (!isNetworkError) {
                get().uiStore?.addToast({
                    type: "warning",
                    title: i18n.t("toasts.form.versionCheckIssue.title"),
                    message: i18n.t("toasts.form.versionCheckIssue.message"),
                    duration: 3000,
                });
            }

            return true;
        }
    },

    setLoading: (loading: boolean) => {
        set((state) => ({
            formState: { ...state.formState, isLoading: loading },
        }));
    },

    setError: (error: string | null) => {
        set((state) => {
            const newFormState = { ...state.formState };
            if (error) {
                newFormState.error = { field: "", message: error, type: "invalid" };
            } else {
                delete newFormState.error;
            }
            return { formState: newFormState };
        });
    },

    reset: () => {
        const { uiStore } = get();

        set({
            formId: "",
            formData: null,
            formMetadata: null,
            bookingDetails: null,
            formState: {
                isLoading: false,
                isSaving: false,
                isSubmitting: false,
                hasUnsavedChanges: false,
                lastSavedVersion: "",
                currentVersion: "",
                error: undefined,
                currentSection: "",
                sections: {},
                versionMismatch: false,
            },
            fileUploadTracker: {},
            // Story 20-2 Phase 1: clear in-memory Guest Memory state on form reset.
            profileMatchByEntryId: {},
            editedFieldsByEntryId: {},
            brandSlug: null,
            brandAutoRedirect: true,
            formLoaded: false,
            contractState: {
                isLoading: false,
                data: null,
                error: null,
            },
            contractSigningState: {
                isLoading: false,
                isSigning: false,
                error: null,
            },
        });

        uiStore?.addToast({
            type: "info",
            title: i18n.t("toasts.form.reset.title"),
            message: i18n.t("toasts.form.reset.message"),
            duration: 3000,
        });
    },

    updateFromServer: (serverData: PublicFormDataAndVersionApiDto, guestEntries?: FormGuestEntry[]) => {
        const { uiStore } = get();

        try {
            if (!serverData) throw new Error('Invalid server response: No data received');
            if (!serverData.data) throw new Error('Invalid server response: Missing data field');
            if (!serverData.version) throw new Error('Invalid server response: Missing version field');

            const newLocalData = convertApiFormDataToLocal(serverData.data, guestEntries);

            // Story 20-1 Phase 1 (refresh-resilient prefill): hydrate profileMatchByEntryId
            // from the public form GET response. Each guest in apiData.{mainGuest,otherGuests}
            // now carries an optional profileMatch (attached by PublicApiImpl._getFormData
            // per guest via lookupProfileMatchByKey). We pair API guests with FE entry IDs
            // using the same logic as convertApiFormDataToLocal so the keying matches.
            const hydratedProfileMatches: Record<string, ProfileMatch> = {};
            if (guestEntries && guestEntries.length > 0) {
                const mainEntry = guestEntries.find(e => e.isMainGuest);
                const nonMainEntries = guestEntries.filter(e => !e.isMainGuest);
                // Pair by the stable entry UUID (guestEntryId); fall back to the guestNumId
                // join only for responses predating guestEntryId. Same identity-based fix as
                // convertApiFormDataToLocal — positional/guestNumId pairing landed a matched
                // guest's photo flags on the wrong entryId (guest image-reuse bug).
                const mainEntryId = serverData.data.mainGuest?.guestEntryId || mainEntry?.id;
                if (serverData.data.mainGuest && mainEntryId && serverData.data.mainGuest.profileMatch) {
                    hydratedProfileMatches[mainEntryId] = serverData.data.mainGuest.profileMatch;
                }
                (serverData.data.otherGuests || []).forEach((g) => {
                    const entryId = g.guestEntryId || nonMainEntries.find(e => e.guestNumId === g.guestNumId)?.id;
                    if (entryId && g.profileMatch) {
                        hydratedProfileMatches[entryId] = g.profileMatch;
                    }
                });
            }

            set((state) => ({
                formData: newLocalData,
                formState: {
                    ...state.formState,
                    currentVersion: serverData.version,
                    lastSavedVersion: serverData.version,
                },
                // Merge hydrated matches over any in-memory ones (post-POST setProfileMatch
                // already populated entries, but a refresh comes through this path too).
                profileMatchByEntryId: {
                    ...state.profileMatchByEntryId,
                    ...hydratedProfileMatches,
                },
            }));
        } catch (error: unknown) {
            console.error('[updateFromServer] Failed to update from server:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to update form data from server';
            get().setError(errorMessage);

            uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.updateFailed.title"),
                message: i18n.t("toasts.form.updateFailed.message"),
                duration: 5000,
            });

            throw error;
        }
    },

    /**
     * Update a single guest's local data by entryId.
     * Replaces updateMainGuestLocal + updateOtherGuestLocal.
     */
    updateGuestLocal: (entryId: string, data: Partial<GuestData>) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g =>
                        g.entryId === entryId ? { ...g, ...data } : g
                    ),
                },
                formState: {
                    ...state.formState,
                    hasUnsavedChanges: true,
                },
            };
        });
    },

    /**
     * Flip the isPrimaryContact flag — sets one guest as primary,
     * clears the flag on all others. No data reshuffling.
     */
    updateGuestDesignation: (entryId: string) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g => ({
                        ...g,
                        isPrimaryContact: g.entryId === entryId,
                    })),
                },
            };
        });
    },

    updateFormStatus: (status: FormStatusApiEnum) => {
        set((state) => ({
            formMetadata: state.formMetadata ? {
                ...state.formMetadata,
                status
            } : null
        }));
    },

    updateGuestFiles: (entryId: string, files: UploadedFileInfo[]) => {
        set((state) => {
            if (!state.formData) return state;

            return {
                formData: {
                    ...state.formData,
                    guests: state.formData.guests.map(g =>
                        g.entryId === entryId ? { ...g, files } : g
                    ),
                },
            };
        });
    },

    updateGuestFilesFromServer: (entryId: string, serverData: PublicFormDataAndVersionApiDto) => {
        const { formData: localData } = get();
        if (!localData) return;

        const targetGuest = localData.guests.find(g => g.entryId === entryId);
        if (!targetGuest) return;

        // convertApiFormDataToLocal resolves entryId from guestEntryId, so match the target
        // guest by its stable entryId; fall back to guestNumId only for legacy responses.
        const newLocalData = convertApiFormDataToLocal(serverData.data);
        const serverGuest = newLocalData.guests.find(g => g.entryId === entryId)
            ?? newLocalData.guests.find(g => g.guestNumId === targetGuest.guestNumId);

        set((state) => ({
            formData: {
                ...state.formData!,
                guests: state.formData!.guests.map(g =>
                    g.entryId === entryId && serverGuest
                        ? { ...g, files: serverGuest.files }
                        : g
                ),
            },
            formState: {
                ...state.formState,
                currentVersion: serverData.version,
                lastSavedVersion: serverData.version,
            },
        }));
    },

    loadContract: async (signal?: AbortSignal) => {
        if (isPreviewMode()) return;
        const { formId } = get();
        set((state) => ({
            contractState: { ...state.contractState, isLoading: true, error: null }
        }));

        try {
            const contractData: ContractApiDto = await formService.getContract(formId, signal);
            if (signal?.aborted) return;

            set(() => ({
                contractState: {
                    isLoading: false,
                    data: contractData,
                    error: null,
                }
            }));
        } catch (error: unknown) {
            const errObj = error as { name?: string; response?: { data?: { message?: string } }; message?: string };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                set((state) => ({
                    contractState: { ...state.contractState, isLoading: false }
                }));
                return;
            }

            console.error("Error loading contract", error);
            const errorMessage = errObj.response?.data?.message || errObj.message || "Failed to load contract";
            set(() => ({
                contractState: {
                    isLoading: false,
                    data: null,
                    error: errorMessage,
                }
            }));

            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.contractLoadError.title"),
                message: errorMessage,
                duration: 3000,
            });
        }
    },

    signContract: async (signature: ContractSignature) => {
        if (isPreviewMode()) return;
        const { formId } = get();
        set((state) => ({
            contractSigningState: { ...state.contractSigningState, isSigning: true, error: null }
        }));

        try {
            const contentType = signature.signatureFile.type as MimeApiEnum;

            const { url, key } = await formService.getContractSignPresignedUrl(formId, {
                contentType: contentType
            });

            const uploadResponse = await fetch(url, {
                method: 'PUT',
                body: signature.signatureFile,
                headers: {
                    'Content-Type': signature.signatureFile.type,
                },
            });

            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                console.error('[signContract] S3 upload failed:', errorText);
                throw new Error(`S3 upload failed with status: ${uploadResponse.status}`);
            }

            const signatureData: ContractSignatureApiDto = {
                formVersion: signature.formVersion,
                s3Key: key,
                contentType: contentType,
                ipAddress: signature.ipAddress,
                deviceInfo: signature.deviceInfo,
            };

            await formService.signContract(formId, signatureData);

            // Signing succeeded. Refresh from server to pick up actual contract validity
            // (the hash-based check runs server-side). Wrap in its own try/catch so a
            // transient refresh failure does NOT surface as a signing failure. NOTE:
            // refreshFormData only re-throws NOT_FOUND errors (other errors are swallowed
            // internally), so the catch here only fires when the form was deleted server-side
            // between sign and refresh. In that case the optimistic "signed=true" would be
            // misleading — the form is gone — so we re-throw to let the FormPage polling /
            // visibility-change handler navigate the user to the not-found state.
            // # WHY guestEntries: without entries, convertApiFormDataToLocal falls back to
            // legacy_<num> entryId placeholders that don't match useGuestRegistrationStore
            // UUIDs — every guest card flashes "Sección pendiente" until the next poll.
            try {
                const guestEntries = useGuestRegistrationStore.getState().guests;
                await get().refreshFormData(formId, undefined, guestEntries);
            } catch (refreshError) {
                console.warn(
                    "[signContract] Sign succeeded but refresh reported NOT_FOUND — form was deleted server-side",
                    refreshError,
                );
                // Do NOT optimistically set signedContract: true — the form no longer exists.
                // Re-throw so the outer catch surfaces the not-found state to the user.
                throw refreshError;
            }

            set(() => ({
                contractSigningState: {
                    isLoading: false,
                    isSigning: false,
                    error: null,
                }
            }));

            get().uiStore?.addToast({
                type: "success",
                title: i18n.t("toasts.form.contractSigned.title"),
                message: i18n.t("toasts.form.contractSigned.message"),
                duration: 3000,
            });
        } catch (error: unknown) {
            console.error("Error signing contract", error);
            const axiosErr = error as { response?: { data?: { message?: string } }; message?: string };
            const errorMessage = axiosErr.response?.data?.message || axiosErr.message || "Failed to sign contract";
            set(() => ({
                contractSigningState: {
                    isLoading: false,
                    isSigning: false,
                    error: errorMessage,
                }
            }));

            get().uiStore?.addToast({
                type: "error",
                title: i18n.t("toasts.form.contractSigningError.title"),
                message: errorMessage,
                duration: 3000,
            });
            throw error;
        }
    },

    setContractError: (error: string | null) => {
        set((state) => ({
            contractState: { ...state.contractState, error }
        }));
    },

    /**
     * Story 20-2 Phase 1: stash the returning-guest profile match for a specific entry,
     * or clear it on `null`. Called by guestRegistrationStore.createEntry immediately
     * after the POST returns (the captured response is the only source — the
     * post-create fetchGuests refresh does NOT carry profileMatch per Decision 23b
     * POST-only).
     *
     * Idempotent. Setting a non-null match initializes editedFieldsByEntryId[entryId]
     * to a fresh empty Set when missing. Clearing the match (`null`) ALSO drops the
     * editedFields slot — a stale edited-fields tracker for an entry whose match has
     * been invalidated would mislead buildRelationalEntryPayload on subsequent saves.
     */
    setProfileMatch: (entryId: string, match: ProfileMatch | null) => {
        set((state) => {
            const nextProfileMatch = { ...state.profileMatchByEntryId };
            const nextEditedFields = { ...state.editedFieldsByEntryId };
            if (match === null) {
                delete nextProfileMatch[entryId];
                delete nextEditedFields[entryId];
            } else {
                nextProfileMatch[entryId] = match;
                if (!nextEditedFields[entryId]) {
                    nextEditedFields[entryId] = new Set<string>();
                }
            }
            return {
                profileMatchByEntryId: nextProfileMatch,
                editedFieldsByEntryId: nextEditedFields,
            };
        });
    },

    /**
     * Story 20-2 Phase 1: add a field name to the per-entry edited-fields set.
     * The Set guarantees idempotency — multiple edits to the same field still
     * register exactly once.
     */
    addEditedField: (entryId: string, fieldName: string) => {
        set((state) => {
            const existing = state.editedFieldsByEntryId[entryId] ?? new Set<string>();
            // Skip the state update if the field is already tracked — avoids re-renders.
            if (existing.has(fieldName)) {
                return state;
            }
            const next = new Set(existing);
            next.add(fieldName);
            return {
                editedFieldsByEntryId: {
                    ...state.editedFieldsByEntryId,
                    [entryId]: next,
                },
            };
        });
    },

    /**
     * Story 20-5 Phase 3 (AC #6): remove a field from the per-entry edited-fields
     * set. Called by MaskedProfileField on Cancel-without-typing to undo the
     * Edit-click registration — without this, a user who taps Edit, types
     * nothing, and taps Cancel would still send `editedFields: [field]` on PUT
     * with an empty value, silently wiping the stored value (data-wipe trap).
     *
     * Idempotent: removing an absent fieldName is a no-op.
     */
    removeEditedField: (entryId: string, fieldName: string) => {
        set((state) => {
            const existing = state.editedFieldsByEntryId[entryId];
            // No set yet for this entry, or the field was never tracked — nothing to do.
            if (!existing || !existing.has(fieldName)) {
                return state;
            }
            const next = new Set(existing);
            next.delete(fieldName);
            return {
                editedFieldsByEntryId: {
                    ...state.editedFieldsByEntryId,
                    [entryId]: next,
                },
            };
        });
    },

    /**
     * Claim 1 follow-up: drop the entire edited-fields slot for an entry. Used by
     * useResetProfileMatchOnIdentityEdit when the lookup-key triple changes after
     * POST — the cross-property profile matched at POST is no longer authoritative,
     * so any user-edited overlay flags from that prior match are stale.
     *
     * Idempotent: clearing an absent slot is a no-op.
     */
    clearEditedFields: (entryId: string) => {
        set((state) => {
            if (!(entryId in state.editedFieldsByEntryId)) {
                return state;
            }
            const next = { ...state.editedFieldsByEntryId };
            delete next[entryId];
            return { editedFieldsByEntryId: next };
        });
    },

    resetContractState: () => {
        set(() => ({
            contractState: {
                isLoading: false,
                data: null,
                error: null,
            },
            contractSigningState: {
                isLoading: false,
                isSigning: false,
                error: null,
            },
        }));
    },

    refreshFormData: async (publicId: string, signal?: AbortSignal, guestEntries?: FormGuestEntry[]) => {
        if (isPreviewMode()) return;
        try {
            const serverResponse: PublicFormDataApiDto = await formService.getForm(publicId, signal);

            if (signal?.aborted) return;

            if (!serverResponse || !serverResponse.data || !serverResponse.version) {
                console.warn('[refreshFormData] Invalid server response, skipping update');
                return;
            }

            const newLocalData = convertApiFormDataToLocal(serverResponse.data, guestEntries);

            set((state) => ({
                formData: newLocalData,
                formMetadata: state.formMetadata ? {
                    ...state.formMetadata,
                    status: serverResponse.status,
                    version: serverResponse.version,
                    overallProgress: serverResponse.overallProgress,
                    signedContract: serverResponse.signedContract,
                    requiredGuestImages: serverResponse.requiredGuestImages,
                    requireContactDataFromAllGuests: serverResponse.requireContactDataFromAllGuests,
                    contractStatus: serverResponse.contractStatus,
                    rejectionReason: serverResponse.rejectionReason,
                    // Story 28-4: propagate the bilingual + multi-status
                    // messages map on every poll refresh so an Owner edit
                    // reflects to an open guest session within the polling
                    // cadence (NFR-LFM-1 2s p95). Always set (possibly empty).
                    formMessages: serverResponse.formMessages ?? {},
                } : null,
                formState: {
                    ...state.formState,
                    currentVersion: serverResponse.version,
                    lastSavedVersion: serverResponse.version,
                },
            }));
        } catch (error: unknown) {
            const errObj = error as { name?: string; code?: string; constraints?: Array<{ type?: string }> };
            if (errObj.name === 'AbortError' || errObj.name === 'CanceledError' || signal?.aborted) {
                return;
            }
            // Re-throw NOT_FOUND so callers (polling) can detect deleted forms
            const isNotFound = errObj.constraints?.some(c => c.type === 'NOT_FOUND') || errObj.code === '404';
            if (isNotFound) {
                throw error;
            }
            console.warn('[refreshFormData] Background refresh failed:', error);
        }
    },

}));
